import { getAndClearReturnPath, saveReturnPath } from './api';

/**
 * The full-page leg of an account link: the popup-blocked fallback in
 * `connectProviderPopup` sends the tab to the provider, and the server's link
 * callback page sends it back to the app root with
 * `?auth_link=<provider>[&auth_link_error=<code>]`. These helpers carry the
 * user back to the page they started from and hand the outcome to
 * `useAuthLinkOutcomeToast`.
 */

const PENDING_LINK_KEY = 'auth_link_pending';
const LINK_OUTCOME_KEY = 'auth_link_outcome';

/** Server error codes are snake_case; anything else is not reported verbatim. */
const ERROR_CODE = /^[a-z][a-z0-9_]{0,63}$/;

interface PendingLink {
  provider: string;
  linkStartPath: string;
}

export interface AuthLinkOutcome extends PendingLink {
  /** Absent on success. */
  error?: string;
}

/** Record a full-page link about to leave for the provider. */
export function rememberPendingLink(provider: string, linkStartPath: string): void {
  saveReturnPath();
  try {
    sessionStorage.setItem(PENDING_LINK_KEY, JSON.stringify({ provider, linkStartPath }));
  } catch {
    // sessionStorage unavailable; the callback still lands on the app.
  }
}

function takeJson(key: string): unknown {
  try {
    const raw = sessionStorage.getItem(key);
    sessionStorage.removeItem(key);
    return raw ? (JSON.parse(raw) as unknown) : null;
  } catch {
    return null;
  }
}

function isPendingLink(value: unknown): value is PendingLink {
  if (!value || typeof value !== 'object') return false;
  const { provider, linkStartPath } = value as Record<string, unknown>;
  return typeof provider === 'string' && typeof linkStartPath === 'string';
}

/**
 * Consume a link outcome from the landing URL: strip its parameters, restore
 * the saved return path, and stash the outcome for the toast.
 *
 * Only an outcome this tab asked for is reported, so a crafted
 * `?auth_link=…&auth_link_error=…` link cannot put words in a toast.
 * `main.tsx` calls it before the first render, so the router never sees the
 * landing URL. Idempotent: a second call finds no parameters.
 */
export function captureAuthLinkOutcome(): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const provider = params.get('auth_link');
  if (provider === null) return;
  const rawError = params.get('auth_link_error');
  params.delete('auth_link');
  params.delete('auth_link_error');
  const rest = params.toString();
  const landingPath = window.location.pathname + (rest ? `?${rest}` : '');

  const pending = takeJson(PENDING_LINK_KEY);
  const ours = isPendingLink(pending) && pending.provider === provider;
  const returnPath = ours ? getAndClearReturnPath() : null;
  if (ours) {
    const outcome: AuthLinkOutcome = { provider, linkStartPath: pending.linkStartPath };
    if (rawError !== null) outcome.error = ERROR_CODE.test(rawError) ? rawError : 'provider_error';
    try {
      sessionStorage.setItem(LINK_OUTCOME_KEY, JSON.stringify(outcome));
    } catch {
      // The toast is optional.
    }
  }
  try {
    window.history.replaceState(null, '', returnPath || landingPath);
  } catch {
    // A broken History API leaves the parameters in place; nothing reads them.
  }
}

/** Drain the outcome stashed by {@link captureAuthLinkOutcome}, once. */
export function takeAuthLinkOutcome(): AuthLinkOutcome | null {
  const value = takeJson(LINK_OUTCOME_KEY);
  if (!isPendingLink(value)) return null;
  const { error } = value as { error?: unknown };
  return {
    provider: value.provider,
    linkStartPath: value.linkStartPath,
    ...(typeof error === 'string' ? { error } : {}),
  };
}
